import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { EChartOption, ECharts } from 'echarts';
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CommonService } from '../../service/common.service';
import { ScatterLineService } from './scatter-line.service'
import { ChartOptionService } from '../../service/chart-option.service';
import { ChartResizeService } from '../../service/chart-resize.service';

@Component({
  selector: 'bcac-scatter-line',
  templateUrl: './scatter-line.component.html',
  styleUrls: ['./scatter-line.component.scss']
})
export class ScatterLineComponent implements OnInit {

  constructor(
    private commonService: CommonService,
    private scatterLineService: ScatterLineService,
    private chartOptionService: ChartOptionService,
    private chartResizeService: ChartResizeService
  ) { }

  chartTitle = {
    show: true,
    label: "图表标题"
  };

  customToolbox = {
    show: true
  }

  // 布局设置
  cardPadding = "1rem";
  cardBgColor = "#fff";
  chartTitleColor = "";

  // echart图表元素
  chartBackgroundColor: string = "#FFF";
  chartColors: string[] = this.chartOptionService.getChartColors('default');
  chartTitles = this.chartOptionService.getChartTitle();
  chartLegend = this.chartOptionService.getChartLegend();
  chartGrid: EChartOption.Grid = this.commonService.changeFontSize().interval;
  chartxAxis: EChartOption.XAxis[] = this.chartOptionService.getChartxAxisValue(1);
  chartyAxis: EChartOption.YAxis[] = this.chartOptionService.getChartyAxis(1);
  chartTextStyle: EChartOption.TextStyle = this.chartOptionService.getTextStyle();
  scatterSeries: EChartOption.SeriesScatter = this.scatterLineService.getDefaultScatterSeries();
  lineSeries = this.scatterLineService.getDefaultLineSeries();
  chartTooltip: EChartOption.Tooltip = this.scatterLineService.getDefaultScatterLineTooltip();
  chartSeriesList: EChartOption.Series[] = [];

  chartOption = {
    backgroundColor: this.chartBackgroundColor,
    title: this.chartTitles,
    legend: this.chartLegend,
    grid: this.chartGrid,
    xAxis: this.chartxAxis,
    yAxis: this.chartyAxis,
    series: this.chartSeriesList,
    tooltip: this.chartTooltip,
    color: this.chartColors,
    textStyle: this.chartTextStyle
  };

  initOpts = this.chartOptionService.getInitOpts();
  optionInit: ECharts;
  onOptionInit(ec) {
    this.optionInit = ec;
  }

  // X轴刻度间隔
  intervalData = [];
  intervalSelectedData;
  intervalSelectedDataChange(e: any) {
    // 更新图表interval
    this.scatterLineService.setInterval(this.chartOption, e);
    this.updateOption();
  }

  axisScaleChange(e: any) {
    this.scatterLineService.setInterval(this.chartOption, e);
    this.updateOption();
  }

  // 接口区
  @Input()
  set bcacDarkMode(darkMode: boolean) {
    if (darkMode == true) {
      this.scatterLineService.changeChartTitleDark(this, darkMode);
      this.scatterLineService.chartBackgroundColorDarkMode(this.chartOption);
      this.scatterLineService.xAxisDarkMode(this.chartxAxis);
      this.scatterLineService.yAxisDarkMode(this.chartyAxis);
      this.scatterLineService.colorsDarkMode(this.chartColors);
      this.scatterLineService.legendDarkMode(this.chartLegend);
      this.scatterLineService.seriesDarkMode(this.scatterSeries);
    }
  }

  @Input()
  set bcacSmallMode(smallMode: boolean) {
    if (smallMode) {
      this.scatterLineService.setSmallModeGrid(this.chartGrid);
      this.scatterLineService.setSmallModePadding(this)
    }
  }

  legendPosition = "bottom";
  @Input()
  set bcacLegendPosition(legendPosition: string) {
    this.legendPosition = legendPosition;
    this.scatterLineService.setLegendPosition(this.chartGrid, this.chartLegend, legendPosition);
  }

  @Input()
  set bcacTitleSwitch(titleSwitch: boolean) {
    this.chartTitle.show = titleSwitch;
  }

  @Input()
  set bcacTitle(title: string) {
    this.chartTitle.label = title;
  }

  @Input()
  set bcacUnit(unit: string[]) {
    this.scatterLineService.setTooltipUnit(this.chartTooltip, unit);
    this.updateOption();
  }

  @Input()
  set bcacXaxisName(xAxisName: string) {
    this.scatterLineService.setXaxisName(this.chartxAxis, xAxisName);
  }

  @Input()
  set bcacYaxisName(yAxisNames: string) {
    this.scatterLineService.setYaxisName(this.chartyAxis, yAxisNames);
  }

  @Input()
  set bcacYnameGap(yNameGap: number) {
    this.scatterLineService.setYNameGap(this.chartyAxis, yNameGap);
  }

  @Input()
  set bcacColors(colorList: string[]) {
    this.scatterLineService.chartColorsChange(this.chartColors, colorList)
  }

  @Input()
  set bcacIntervalData(value: any[]) {
    this.intervalData = value;
  }

  // 加载等待
  _bcacChartLoading: boolean = false
  @Input()
  set bcacChartLoading(loading: boolean) {
    this._bcacChartLoading = loading;
  }

  // 系列的数据维度名
  @Input()
  set bcacScatterDimensions(dimensions: string[]) {
    this.scatterLineService.setScatterDimensions(dimensions, this.scatterSeries)
  }

  @Input()
  set bcacSymbolSize(size: number) {
    this.scatterLineService.setSymbolSize(size, this.scatterSeries)
  }

  // 数据
  legendDataList;
  @Input()
  set bcacChartData(chartData: any) {

    let legendDataList = [];
    if (chartData && chartData.hasOwnProperty('seriesName')) {
      chartData.seriesName.forEach(element => {
        legendDataList.push(
          {
            label: element,
            value: element,
            checked: true
          }
        )
      });
    }
    this.legendDataList = legendDataList;

    if (chartData && chartData.hasOwnProperty('tooltipUnit')) {
      this.scatterLineService.setTooltipUnit(this.chartTooltip, chartData.tooltipUnit);
    }

    this.scatterLineService.buildSeries(chartData, this.chartSeriesList, this.chartxAxis, this.scatterSeries, this.lineSeries)

    this.updateOption();
  }

  // x轴label旋转
  @Input()
  set bcacXAxisLabelRotate(xAxisLabelRotate) {
    this.scatterLineService.setXAxisLabelRotate(this.chartxAxis, xAxisLabelRotate)
  }

  dataSourceSelectData = [];
  @Input()
  set bcacDataSourceList(value: any[]) {
    this.dataSourceSelectData = value;
  }

  legendChange($event) {
    this.scatterLineService.topLegendChange($event, this.optionInit);
  }

  @Input()
  set bcacYAxisMin(yAxisMin: number | undefined | null) {
    this.scatterLineService.setYAxisMin(this.chartyAxis, yAxisMin);
  }

  @Input()
  set bcacYAxisMax(yAxisMax: number | undefined | null) {
    this.scatterLineService.setYAxisMax(this.chartyAxis, yAxisMax);
  }

  @Input()
  set bcacYAxisInterval(yAxisInterval: number) {
    this.scatterLineService.setYAxisInterval(this.chartyAxis, yAxisInterval)
  }

  @Input()
  set bcacSplitLineShow(splitLineShow: boolean | boolean[]) {
    this.scatterLineService.setSplitLineShow(this.chartxAxis, this.chartyAxis, splitLineShow)
  }

  @Output()
  bcacDataSourceSelectEmit = new EventEmitter<any>();
  dataSourceChange(e: any) {
    this.bcacDataSourceSelectEmit.emit(e);
  }
  options;
  // echart配置项
  initEchart() {
    this.options = this.chartOption;
  }

  updateOption() {
    if (this.optionInit) {
      this.optionInit.setOption(this.options, true)
    }
  }

  ngOnInit() {
    this.initEchart();
    this.updateOption();
    fromEvent(window, 'resize').pipe(debounceTime(200)).subscribe(() => {
      this.chartResizeService.autoResizeAdapt(this.optionInit, this.options)
    })

  }

}
